import { UseFormSetValue } from 'react-hook-form'

import { AdditionalDriverItem, State } from 'directFlow/state'
import { isUKLicenceType } from 'directFlow/utils'

import { LicenceType } from 'types'
import { DateObject } from 'src/types/date'
import { isCompleteDateObjectType } from 'utils/date/date'

export const padSingleDigitWithZero = (input: string): string => {
  if (input.length === 1) {
    return '0' + input
  }
  return input
}

export const replaceMacWithMc = (lastName: string): string => {
  if (lastName.toLowerCase().startsWith('mac')) {
    // Replace "Mac" with "Mc" at the beginning of the string
    return 'Mc' + lastName.slice(3)
  }
  return lastName
}

export const generateLicenceNumberNameSection = (lastName: string | undefined) => {
  if (!lastName) {
    return ''
  }

  // If a surnames starts with Mac with replace with Mc for DLN
  // https://www.gov.uk/government/publications/dbs-identity-checking-guidelines/id-checking-guidelines-for-standardenhanced-dbs-check-applications-from-1-july-2021#checking-driving-licences
  const transformedLastName = replaceMacWithMc(lastName)

  // replacing whitespace, hyphens and apostrophes with empty string
  const noWhitespaceLastName = transformedLastName.replace(/[\s\-']/g, '')

  if (noWhitespaceLastName.length >= 5) {
    return noWhitespaceLastName.substring(0, 5).toUpperCase()
  } else {
    const missingChars = 5 - noWhitespaceLastName.length
    const replacement = '9'.repeat(missingChars)
    return noWhitespaceLastName.toUpperCase() + replacement.toUpperCase()
  }
}

export const generateDrivingLicenceDateOfBirthSection = (
  title: string | undefined,
  dateOfBirth: DateObject,
) => {
  if (!title || !dateOfBirth.day || !dateOfBirth.month || !dateOfBirth.year) {
    return ''
  }

  const uppercaseTitle = title.toUpperCase()
  const yearOfBirth = dateOfBirth.year
  // For gender-neutral titles, we can only prefill up to this point since the next digit depends on gender
  if (uppercaseTitle === 'MX' || uppercaseTitle === 'DR') {
    return yearOfBirth[2]
  }

  const dayOfBirth = padSingleDigitWithZero(dateOfBirth.day)
  const monthOfBirth =
    title.toUpperCase() === 'MR'
      ? padSingleDigitWithZero(dateOfBirth.month)
      : String(Number(dateOfBirth.month) + 50)

  return `${yearOfBirth[2]}${monthOfBirth}${dayOfBirth}${yearOfBirth[3]}`
}

export const generateDrivingLicenceNumberPrefill = ({
  title,
  lastName,
  dateOfBirth,
}: {
  title: string | undefined
  lastName: string | undefined
  dateOfBirth: DateObject | undefined | null
}) => {
  if (!lastName || !isCompleteDateObjectType(dateOfBirth) || !title) {
    return ''
  }

  const nameSection = generateLicenceNumberNameSection(lastName)
  const dateOfBirthSection = generateDrivingLicenceDateOfBirthSection(title, dateOfBirth)

  return `${nameSection}${dateOfBirthSection}`
}

interface GenerateDrivingLicenceNumberArgs {
  licenceType?: LicenceType
}

export const showLicenceNumberQuestion = ({ licenceType }: GenerateDrivingLicenceNumberArgs) => {
  const hasUkLicence = licenceType && isUKLicenceType(licenceType)

  return hasUkLicence
}

type SetValueFunction = UseFormSetValue<State['drivers'] | AdditionalDriverItem>

export const preFillDrivingLicenceNumber = (
  dateOfBirth: DateObject | null,
  lastName: string,
  title: string,
  setValue: SetValueFunction,
) => {
  const preFill = generateDrivingLicenceNumberPrefill({
    dateOfBirth,
    lastName,
    title,
  })

  setValue('drivingLicenceNumber', preFill)
}
